import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from "react";
import {
  useGridApiContext,
  useGridSelector,
  gridExpandedSortedRowEntriesSelector,
  gridVisibleColumnDefinitionsSelector,
} from "@mui/x-data-grid-premium";
import {
  Box,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FindInPage as FindInPageIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import "./FindInGrid.css";
import {
  isFindInGridShortcut,
  registerFindInGridTarget,
} from "./jumpToColumnRegistry";
import {
  cellSearchText,
  findMatches,
  stepMatch,
  type FindColumn,
  type FindMatch,
} from "./findInGrid";

const HIGHLIGHT_CLASS = "sdg-find-in-grid-highlight";
const DEBOUNCE_MS = 150;
// Frames to wait for the scroll to settle and the (virtualized) cell to render
const MAX_WAIT_FRAMES = 60;
// scrollToIndexes keeps reporting a scroll for a cell wider than the viewport
const MAX_SCROLL_ATTEMPTS = 3;
// Selection and detail-panel toggles have no text
const SKIPPED_FIELDS = new Set(["__check__", "__detail_panel_toggle__"]);

interface FindInGridProps {
  // Element that counts as "inside this grid" when routing Ctrl+F between
  // grids; defaults to the grid root
  rootRef?: RefObject<HTMLElement | null>;
}

/**
 * Toolbar button + Ctrl+F box that steps through the cells containing some
 * text, scrolling each into view and marking it. Row and column
 * virtualization keep off-screen cells out of the DOM, so the browser's own
 * Ctrl+F can't see them; this searches the grid's row data instead. Scope:
 * the rows loaded on the current page and the visible columns.
 */
export function FindInGrid({ rootRef }: FindInGridProps) {
  const apiRef = useGridApiContext();
  // The grid paginates server-side (dataSource), so the expanded sorted rows
  // are exactly the current page in display order. (The paginated-rows
  // selector is empty in server mode: its page range is client-side only.)
  const rowEntries = useGridSelector(
    apiRef,
    gridExpandedSortedRowEntriesSelector
  );
  const visibleColumns = useGridSelector(
    apiRef,
    gridVisibleColumnDefinitionsSelector
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [current, setCurrent] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const frameRef = useRef<number | null>(null);
  const highlightRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Ctrl+F goes through the shared registry so only one grid's box opens
  // when several grids are on the page
  useEffect(
    () =>
      registerFindInGridTarget({
        getRoot: () =>
          rootRef?.current ?? apiRef.current?.rootElementRef?.current ?? null,
        open: () => {
          // Bring the toolbar on screen so the box isn't anchored off-screen
          buttonRef.current?.scrollIntoView?.({ block: "nearest" });
          setOpen(true);
        },
      }),
    [apiRef, rootRef]
  );

  const matches = useMemo<FindMatch[]>(() => {
    const api = apiRef.current;
    if (!api || !open || !debouncedQuery.trim()) return [];
    const columns: FindColumn[] = [];
    visibleColumns.forEach((column, colIndex) => {
      if (!SKIPPED_FIELDS.has(column.field)) {
        columns.push({ field: column.field, colIndex });
      }
    });
    return findMatches(
      rowEntries.map((entry) => entry.id),
      columns,
      (id, field) => {
        try {
          return cellSearchText(api.getCellParams(id, field));
        } catch {
          return ""; // e.g. a group row for a column it has no value in
        }
      },
      debouncedQuery
    );
  }, [apiRef, open, debouncedQuery, rowEntries, visibleColumns]);

  // A new query, page or data set starts over from the first match
  useEffect(() => {
    setCurrent(matches.length > 0 ? 0 : -1);
  }, [matches]);

  const cancelReveal = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  const clearHighlight = useCallback(() => {
    highlightRef.current?.classList.remove(HIGHLIGHT_CLASS);
    highlightRef.current = null;
  }, []);

  useEffect(
    () => () => {
      cancelReveal();
      clearHighlight();
    },
    [cancelReveal, clearHighlight]
  );

  const reveal = useCallback(
    (match: FindMatch) => {
      cancelReveal();
      clearHighlight();
      let frames = 0;
      let scrollAttempts = 0;

      const step = () => {
        frameRef.current = null;
        const api = apiRef.current;
        if (!api || ++frames > MAX_WAIT_FRAMES) return;
        const next = () => {
          frameRef.current = requestAnimationFrame(step);
        };

        // Pinned columns are always on screen; scrolling to one would only
        // move the unpinned area. scrollToIndexes returns true when it moved.
        const pinned = api.isColumnPinned(match.field);
        if (
          scrollAttempts < MAX_SCROLL_ATTEMPTS &&
          api.scrollToIndexes({
            rowIndex: match.rowIndex,
            colIndex: pinned ? undefined : match.colIndex,
          })
        ) {
          scrollAttempts++;
          return next();
        }

        // Virtualized cells render after the scroll event
        const cell = api.getCellElement(match.id, match.field);
        if (!cell) return next();
        cell.classList.add(HIGHLIGHT_CLASS);
        highlightRef.current = cell;
      };

      frameRef.current = requestAnimationFrame(step);
    },
    [apiRef, cancelReveal, clearHighlight]
  );

  useEffect(() => {
    if (!open || current < 0 || !matches[current]) {
      cancelReveal();
      clearHighlight();
      return;
    }
    reveal(matches[current]);
  }, [open, current, matches, reveal, cancelReveal, clearHighlight]);

  const close = () => {
    setOpen(false);
    const match = current >= 0 ? matches[current] : undefined;
    if (match) {
      // Hand the keyboard to the found cell
      try {
        apiRef.current.setCellFocus(match.id, match.field);
      } catch {
        // The cell may have left the page since
      }
    }
  };

  const go = (direction: 1 | -1) =>
    setCurrent((index) => stepMatch(index, matches.length, direction));

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      go(e.shiftKey ? -1 : 1);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (isFindInGridShortcut(e)) {
      // Already open: keep the browser's find closed and reselect the text
      e.preventDefault();
      inputRef.current?.select();
    }
  };

  const count = matches.length;
  const status = !debouncedQuery.trim()
    ? ""
    : count === 0
    ? "No matches"
    : `${current + 1} of ${count}`;

  return (
    <>
      <Tooltip title="Find in grid (Ctrl+F)">
        <IconButton
          ref={buttonRef}
          size="small"
          aria-label="Find in grid"
          onClick={() => setOpen(true)}
        >
          <FindInPageIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 400, p: 1.5 } } }}
        data-testid="find-in-grid"
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Find in grid
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Find text..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            inputProps={{ "aria-label": "Find in grid" }}
          />
          <Typography
            variant="body2"
            color={count === 0 && status ? "error" : "text.secondary"}
            sx={{ whiteSpace: "nowrap", minWidth: 64, textAlign: "center" }}
            data-testid="find-in-grid-status"
          >
            {status}
          </Typography>
          <IconButton
            size="small"
            aria-label="Previous match"
            disabled={count === 0}
            onClick={() => go(-1)}
          >
            <KeyboardArrowUpIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Next match"
            disabled={count === 0}
            onClick={() => go(1)}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 1 }}
        >
          Searches this page's rows and visible columns. Enter: next,
          Shift+Enter: previous.
        </Typography>
      </Popover>
    </>
  );
}
